import axios from 'axios';

// 使用相对路径API URL，适合生产环境nginx代理
const API_URL = process.env.VUE_APP_API_URL || '/api';

// 全局axios配置
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.put['Content-Type'] = 'application/json';
axios.defaults.headers.patch['Content-Type'] = 'application/json';

// 图片上传配置
const uploadConfig = {
  headers: {
    'Content-Type': 'application/json'
  },
  maxContentLength: 50 * 1024 * 1024 // 50MB
};

// 设置认证token
export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

// 从localStorage获取token
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

// 初始化token
const token = getAuthToken();
if (token) {
  setAuthToken(token);
}

// 认证相关API
export const authAPI = {
  register: async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    if (response.data.token) {
      setAuthToken(response.data.token);
    }
    return response.data;
  },

  login: async (credentials) => {
    const response = await axios.post(`${API_URL}/auth/login`, credentials);
    if (response.data.token) {
      setAuthToken(response.data.token);
    }
    return response.data;
  },

  verify: async () => {
    const response = await axios.get(`${API_URL}/auth/verify`);
    return response.data;
  },

  logout: () => {
    setAuthToken(null);
  }
};

// 图片上传相关API
export const uploadsAPI = {
  uploadImage: async (imageData, filename) => {
    const response = await axios.post(`${API_URL}/uploads/image`, {
      image: imageData,
      filename: filename
    }, uploadConfig);
    return response.data;
  }
};

// 团队相关API
export const teamsAPI = {
  getMyTeams: async () => {
    const response = await axios.get(`${API_URL}/teams/my-teams`);
    return response.data;
  },

  createTeam: async (teamData) => {
    const response = await axios.post(`${API_URL}/teams`, teamData);
    return response.data;
  },

  getTeam: async (teamId) => {
    const response = await axios.get(`${API_URL}/teams/${teamId}`);
    return response.data;
  },

  joinTeam: async (inviteCode) => {
    const response = await axios.post(`${API_URL}/teams/join`, { inviteCode });
    return response.data;
  },

  getTeamMembers: async (teamId) => {
    const response = await axios.get(`${API_URL}/teams/${teamId}/members`);
    return response.data;
  }
};

// 获取所有看板
export const getBoards = async (teamId) => {
  try {
    const response = await axios.get(`${API_URL}/boards`, {
      params: { teamId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching boards:', error);
    throw error;
  }
};

export const getBoard = async (boardId) => {
  try {
    const response = await axios.get(`${API_URL}/boards/${boardId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching board:', error);
    throw error;
  }
};

// 创建看板
export const createBoard = async (boardData) => {
  try {
    const response = await axios.post(`${API_URL}/boards`, boardData);
    return response.data;
  } catch (error) {
    console.error('Error creating board:', error);
    throw error;
  }
};

export const updateBoard = async (boardId, boardData) => {
  try {
    const response = await axios.put(`${API_URL}/boards/${boardId}`, boardData);
    return response.data;
  } catch (error) {
    console.error('Error updating board:', error);
    throw error;
  }
};

export const deleteBoard = async (boardId) => {
  try {
    await axios.delete(`${API_URL}/boards/${boardId}`);
  } catch (error) {
    console.error('Error deleting board:', error);
    throw error;
  }
};

// 获取指定看板的列表
export const getListsByBoardId = async (boardId) => {
  try {
    const response = await axios.get(`${API_URL}/lists?boardId=${boardId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching lists by boardId:', error);
    throw error;
  }
};

// 列表相关API
export const getLists = async () => {
  try {
    const response = await axios.get(`${API_URL}/lists`);
    return response.data;
  } catch (error) {
    console.error('Error fetching lists:', error);
    throw error;
  }
};

export const createList = async (listData) => {
  try {
    const response = await axios.post(`${API_URL}/lists`, listData);
    return response.data;
  } catch (error) {
    console.error('Error creating list:', error);
    throw error;
  }
};

export const updateList = async (listId, listData) => {
  try {
    const response = await axios.patch(`${API_URL}/lists/${listId}`, listData);
    return response.data;
  } catch (error) {
    console.error('Error updating list:', error);
    throw error;
  }
};

export const deleteList = async (listId) => {
  try {
    await axios.delete(`${API_URL}/lists/${listId}`);
  } catch (error) {
    console.error('Error deleting list:', error);
    throw error;
  }
};

// 任务相关API

export const createTask = async (taskData) => {
  try {
    const response = await axios.post(`${API_URL}/tasks`, taskData);
    return response.data;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

export const updateTask = async (taskId, taskData) => {
  try {
    const response = await axios.patch(`${API_URL}/tasks/${taskId}`, taskData);
    return response.data;
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

export const deleteTask = async (taskId) => {
  try {
    await axios.delete(`${API_URL}/tasks/${taskId}`);
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// 镜像相关API
export const getMirrorStatus = async (listId) => {
  try {
    const response = await axios.get(`${API_URL}/lists/${listId}/mirror-status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching mirror status:', error);
    throw error;
  }
};

export const createMirrorList = async (listId, targetBoardId) => {
  try {
    const response = await axios.post(`${API_URL}/lists/${listId}/create-mirror`, {
      targetBoardId
    });
    return response.data;
  } catch (error) {
    console.error('Error creating mirror list:', error);
    // 处理后端返回的错误消息
    if (error.response && error.response.data && error.response.data.message) {
      const newError = new Error(error.response.data.message);
      throw newError;
    }
    throw error;
  }
};

export const removeMirrorList = async (listId, mirrorId) => {
  try {
    await axios.delete(`${API_URL}/lists/${listId}/mirror/${mirrorId}`);
  } catch (error) {
    console.error('Error removing mirror list:', error);
    // 处理后端返回的错误消息
    if (error.response && error.response.data && error.response.data.message) {
      const newError = new Error(error.response.data.message);
      throw newError;
    }
    throw error;
  }
};

export const updateMirrorConfig = async (listId, mirrorConfig) => {
  try {
    const response = await axios.patch(`${API_URL}/lists/${listId}`, {
      mirrorConfig
    });
    return response.data;
  } catch (error) {
    console.error('Error updating mirror config:', error);
    throw error;
  }
};

export const updateTaskOrder = async (listId, taskOrder) => {
  try {
    const response = await axios.patch(`${API_URL}/lists/${listId}/task-order`, {
      taskOrder
    });
    return response.data;
  } catch (error) {
    console.error('Error updating task order:', error);
    throw error;
  }
};